import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminLogin = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    setLoading(true);
    // TODO: Integrate with backend Google authentication
    // For now, just simulate login and redirect
    setTimeout(() => {
      // Store mock auth token (will be replaced with real auth)
      localStorage.setItem('adminToken', 'mock-token');
      navigate('/admin/dashboard');
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e] relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="w-full max-w-5xl px-4 sm:px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
          {/* Left Side - Branding */}
          <div className="hidden md:block space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <img 
                  src="/logo.jpg" 
                  alt="Prodizzy Logo"
                  className="h-16 w-16 rounded-full object-cover border-2 border-purple-400/50 shadow-lg shadow-purple-500/50"
                />
                <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Prodizzy
                </h1>
                <p className="text-sm text-purple-300">Real World Product Challenge</p>
              </div>
            </div>
            
            <div className="space-y-4">
              
              <p className="text-sm text-gray-400 leading-relaxed">
                Manage projects, review applications, and oversee the platform with powerful admin tools.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-purple-500/20">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Secure Access</p>
                <p className="text-xs text-gray-400">Google OAuth 2.0 authentication</p>
              </div>
            </div>
          </div>

          {/* Right Side - Login Card */}
          <div className="w-full">
            <div className="bg-gradient-to-br from-[#2d1b4e]/95 to-[#1a0b2e]/95 backdrop-blur-xl rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 border border-purple-500/40 shadow-2xl relative overflow-hidden">
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-full blur-3xl -ml-24 -mb-24"></div>
              
              <div className="relative z-10">
                {/* Mobile Logo */}
                <div className="md:hidden flex items-center gap-3 mb-8 justify-center">
                  <div className="relative">
                    <img 
                      src="/logo.jpg" 
                      alt="Prodizzy Logo"
                      className="h-14 w-14 rounded-full object-cover border-2 border-purple-400/50 shadow-lg shadow-purple-500/50"
                    />
                    <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-pulse"></div>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                      Prodizzy
                    </h1>
                    <p className="text-xs text-purple-300">Admin Panel</p>
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-6 sm:mb-8 md:mb-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500/40 to-pink-500/40 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 border border-purple-400/50 shadow-lg shadow-purple-500/30 relative">
                    <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-purple-200" />
                    <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-400/20 to-pink-400/20 animate-pulse"></div>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3">
                    Welcome Back
                  </h2>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Sign in to access the admin dashboard
                  </p>
                </div>

                {/* Google Login Button */}
                <Button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full h-12 sm:h-14 bg-white text-gray-900 hover:bg-gray-50 font-semibold text-sm sm:text-base shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
                  variant="default"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <>
                      {/* Shimmer effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      <div className="flex items-center justify-center gap-3 relative z-10">
                        {/* Google Logo SVG */}
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span className="text-base">Sign in with Google</span>
                      </div>
                    </>
                  )}
                </Button>

                {/* Info Note */}
                <div className="mt-8 p-5 bg-gradient-to-br from-purple-500/15 to-pink-500/10 rounded-xl border border-purple-500/30 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg flex-shrink-0">
                      <Shield className="w-5 h-5 text-purple-300" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-purple-200 mb-1">Secure Authentication</p>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        Only authorized administrators with verified Google accounts can access this panel. Your session is secured with OAuth 2.0.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;

